/* Northsheep — withdrawal / return request.
 *
 * A port of the old theme's inline script, with the same three-step flow and
 * the same payload keys, so the n8n workflow behind it needs no change:
 *
 *     fill in  ->  review what you are declaring  ->  send
 *
 * The review step is not decoration. A withdrawal is a legal declaration, and
 * a shopper should read theirs before it is recorded.
 *
 * What changed: the market is no longer written into the script. The old
 * version carried store/language/country/policy_version as literals, which is
 * precisely why the form lived on two of five stores — a new market meant
 * editing JavaScript. It now reads them from a JSON block the section
 * renders, so one file serves every store.
 */
(function () {
  'use strict';

  function setup(section) {
    var form = section.querySelector('[data-ns-return-form]');
    var review = section.querySelector('[data-ns-return-review]');
    var summary = section.querySelector('[data-ns-return-summary]');
    var success = section.querySelector('[data-ns-return-success]');
    var error = section.querySelector('[data-ns-return-error]');
    var back = section.querySelector('[data-ns-return-back]');
    var send = section.querySelector('[data-ns-return-send]');
    var configEl = section.querySelector('[data-ns-return-config]');
    if (!form || !review || !summary || !configEl) return;

    var cfg;
    try {
      cfg = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    var payload = null;

    function read() {
      var data = new FormData(form);
      var get = function (k) {
        return String(data.get(k) || '').trim();
      };
      return {
        /* These key names are the contract with the existing workflow. Do not
         * rename one without changing the workflow at the same time. */
        request_type: 'withdrawal_return_request',
        source: 'shopify_theme_return_section',
        store: cfg.store,
        language: cfg.language,
        country: cfg.country,
        return_page_url: window.location.href,
        return_policy_version: cfg.policy_version,
        customer_name: get('customer_name'),
        customer_email: get('customer_email'),
        order_number: get('order_number'),
        received_date: get('received_date'),
        products: get('products'),
        message: get('message'),
        withdrawal_confirm: Boolean(data.get('withdrawal_confirm')),
        website: get('website'),
        page_url: window.location.href,
        shop_domain: (window.Shopify && window.Shopify.shop) || window.location.hostname,
        submitted_at_browser: new Date().toISOString(),
      };
    }

    function renderSummary(data) {
      summary.textContent = '';
      Object.keys(cfg.labels).forEach(function (key) {
        var value = data[key];
        /* An empty optional field is shown as a dash rather than omitted, so
         * the shopper can see it was left blank on purpose. */
        var wrap = document.createElement('div');
        var dt = document.createElement('dt');
        var dd = document.createElement('dd');
        dt.textContent = cfg.labels[key];
        dd.textContent = value === '' || value == null ? '—' : String(value);
        wrap.appendChild(dt);
        wrap.appendChild(dd);
        summary.appendChild(wrap);
      });
    }

    function show(el) {
      [review, success, error].forEach(function (p) {
        if (p) p.hidden = p !== el;
      });
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      /* Red borders only from the first attempt on (audit 2 Oct 2026: the
       * fields were red before anyone had typed - the attribute the CSS
       * waits for was never set). */
      form.removeAttribute('data-ns-pristine');
      if (error) error.hidden = true;
      if (success) success.hidden = true;

      /* The browser's own validation, so the messages arrive translated and
       * the focus lands on the offending field without any of it being
       * reimplemented here. */
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      payload = read();
      renderSummary(payload);
      form.hidden = true;
      show(review);
    });

    if (back) {
      back.addEventListener('click', function () {
        review.hidden = true;
        form.hidden = false;
        form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    if (send) {
      send.addEventListener('click', function () {
        if (!payload) return;
        if (!cfg.endpoint) {
          /* No endpoint configured on this store: say so rather than
           * pretending to send. The fallback email in the error panel is the
           * shopper's way out, and a withdrawal must always have one. */
          show(error);
          return;
        }
        send.disabled = true;
        var label = send.textContent;
        send.textContent = cfg.sending || label;

        /* The press of "Confirm withdrawal" IS the confirmation (EU
         * withdrawal button, in force 19 June 2026), so the tick box that
         * used to set this is gone. The key stays in the payload, true, so
         * the workflow reads exactly what it always has. */
        payload.withdrawal_confirm = true;

        fetch(cfg.endpoint, {
          method: 'POST',
          /* text/plain on purpose, exactly as the old form sent it: it keeps
           * the request a CORS "simple" one, so the browser sends no preflight
           * and the webhook needs no OPTIONS handler. */
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload),
        })
          .then(function (res) {
            if (!res.ok) throw new Error('bad status ' + res.status);
            show(success);
          })
          .catch(function () {
            show(error);
          })
          .finally(function () {
            send.disabled = false;
            send.textContent = cfg.send || label;
          });
      });
    }
  }

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('.ns-return'), setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();


/* ------------------------------------------------------------
   PRINT THE MODEL WITHDRAWAL FORM

   WebwinkelKeur asked for a printable model form on the Dutch return page, and
   German and French law require the model form to be provided too. Rather than
   host a PDF per country — five files to keep in step with five sets of
   company details — the form is text on the page and this prints it.

   The approach is a body class plus a print stylesheet: everything except the
   marked block is hidden at print time, so the customer gets the form and not
   the header, the form fields and the footer. The class is removed straight
   after, because window.print() blocks until the dialog closes in every
   browser that matters, and leaving it on would blank the page if it did not.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-ns-print-model]');
    if (!btn) return;

    var block = btn.closest('[data-ns-model-form]');
    if (!block) return;

    /* The sheet prints, not the whole block: the heading, the lead line and
       the buttons above it are for the screen. It lives in a <details>,
       whose content a browser does not print while it is closed, so it is
       opened for the print and closed again after if it was closed before. */
    var sheet = block.querySelector('[data-ns-model-sheet]') || block;
    var details = block.querySelector('[data-ns-model-details]');
    var wasOpen = details ? details.open : true;
    if (details) details.open = true;

    sheet.classList.add('ns-print-target');
    document.body.classList.add('ns-printing-model');

    /* Cleared on afterprint, not straight after print(): a desktop browser
       blocks in print() until the dialog closes, but a phone returns at once
       and prints afterwards, and clearing then would print the whole page. */
    var done = false;
    function cleanup() {
      if (done) return;
      done = true;
      window.removeEventListener('afterprint', cleanup);
      document.body.classList.remove('ns-printing-model');
      sheet.classList.remove('ns-print-target');
      if (details && !wasOpen) details.open = false;
    }
    window.addEventListener('afterprint', cleanup);
    window.print();
    if (!('onafterprint' in window)) setTimeout(cleanup, 1000);
  });
})();
